import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  Checkbox,
  IconButton,
  SelectChangeEvent,
  Switch,
  TableCell,
  TableRow,
  useTheme,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { FormSelect } from 'src/components/form-select'
import { AcccountsListButton } from './accounts-list-button.component'
import { AccountBadgedIcon } from './account-badge-icon.component'
import { UserMoreIcon } from './user-more-icon.component'
import { UserAvatar } from './user-avatar.component'
import { useEditUserRoles, useMe } from 'src/hooks'

import { EUserRoles, IProfileUserResponse } from 'src/types'
import { useStyles } from '../styles'
import { getTableTimestamp, isAccountOwner, joinAbsRoute, truncateString } from 'src/common/utils'
import { AppEvents, ROUTES } from 'src/constants'

interface ITableBodyRowProps {
  row: IProfileUserResponse
  handleItemCheck: Function
  isItemSelected: (id: string) => boolean
}

const selectItems = Object.entries(EUserRoles).map(([label, value]) => ({ label, value }))

const TableBodyRow = ({ row, handleItemCheck, isItemSelected }: ITableBodyRowProps) => {
  const [t] = useTranslation()
  const [roles, setRoles] = useState(row.roles)
  const { mutateAsync: changeUserRoles, isLoading } = useEditUserRoles()
  const { data: me } = useMe()
  const classes = useStyles()
  const theme = useTheme()
  const navigate = useNavigate()

  const handleRoleChange = async (e: SelectChangeEvent<EUserRoles[]>) => {
    const roles = e.target.value

    if (!Array.isArray(roles)) return

    if (!me || me.id === row.id) {
      const detail = { message: t('users-change-role-warning.v') }

      const event = new CustomEvent(AppEvents.HTTP_ERROR, { detail })
      return document.dispatchEvent(event)
    }

    setRoles(roles)
    await changeUserRoles({ roles, id: row.id })
  }

  const handleAccountClick = (id: string) => () =>
    navigate(joinAbsRoute(ROUTES.ACCOUNTS, id, ROUTES.DASHBOARD))

  const selectRenderValue = (selected: EUserRoles[]) => {
    return selected.includes(EUserRoles.superadmin) ? 'superadmin' : selected.join(', ')
  }

  return (
    <TableRow
      key={row.id}
      className={classes.tableRow}
      sx={{ 'td, th': { border: 0, cursor: 'default', p: '16px 8px' } }}
    >
      <TableCell>
        <Checkbox
          checked={isItemSelected(row.id)}
          sx={{ color: theme.palette.custom.grey2 }}
          onChange={handleItemCheck(row.id)}
        />
      </TableCell>
      <TableCell>
        <UserAvatar
          withPopover
          mobile={row.mobile}
          alt={row.name ?? row.email}
          src={row.image_small || row.image}
          suspended={row.suspended}
        />
      </TableCell>
      <TableCell>{row.email}</TableCell>
      <TableCell align="center">
        <div className={classes.iconsWrapper}>
          {row?.accounts?.map(({ account, roles, status }, zIndex) => {
            if (zIndex < 3 && account) {
              return (
                <IconButton
                  style={{ zIndex }}
                  key={account.id}
                  onClick={handleAccountClick(account.id)}
                  className={classes.selectionIconButton}
                >
                  <AccountBadgedIcon
                    alt={account.account_holder}
                    src={account.image_small || account.image}
                    isOwner={isAccountOwner(roles, status)}
                  />
                </IconButton>
              )
            }
            return null
          })}
          {row?.accounts?.length >= 4 && <AcccountsListButton accounts={row.accounts.slice(3)} />}
        </div>
      </TableCell>
      <TableCell align="center">
        <FormSelect
          noClear
          renderValue={selectRenderValue}
          disabled={isLoading}
          value={roles}
          handleChange={handleRoleChange}
          items={selectItems}
          multiple
        />
      </TableCell>
      <TableCell align="center">
        <Switch size="small" disableRipple tabIndex={-1} checked={row.activated} />
      </TableCell>
      <TableCell align="center">
        <Switch size="small" tabIndex={-1} disableRipple checked={row.agreement} />
      </TableCell>
      <TableCell align="center" sx={{ fontSize: '10px' }}>
        {getTableTimestamp({ date: row.created_at, timezone: me?.timezone })}
      </TableCell>
      <TableCell align="center" sx={{ fontSize: '10px' }}>
        {getTableTimestamp({ date: row.updated_at, timezone: me?.timezone })}
      </TableCell>
      <TableCell>
        <UserMoreIcon user={row} />
      </TableCell>
    </TableRow>
  )
}

export { TableBodyRow }
